import { Scroll, Preload, ScrollControls } from '@react-three/drei'
import '../styles.css'
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import { MathJaxContext } from 'better-react-mathjax';
import { MathJaxProvider, MathJaxFormula } from 'mathjax3-react';
import React, {useEffect} from 'react';
import Markdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import 'katex/dist/katex.min.css' // `rehype-katex` does not import the CSS for you
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter'
import {dark, vscDarkPlus} from 'react-syntax-highlighter/dist/esm/styles/prism'
import BlogLightbox from "./BlogLightbox";
import TableOfContent from "./TableOfContent";

export default function BlogPostSIFT() {
    const [openFig1, setOpenFig1] = React.useState(false);
    const [openFig2, setOpenFig2] = React.useState(false);
    const [openFig3, setOpenFig3] = React.useState(false);
    const [openFig4, setOpenFig4] = React.useState(false);
    const [openFig5, setOpenFig5] = React.useState(false);
    const [openFig6, setOpenFig6] = React.useState(false);
    const [openFig7, setOpenFig7] = React.useState(false);
    const [openFig8, setOpenFig8] = React.useState(false);
    const [openFig9, setOpenFig9] = React.useState(false);
    const [openFig10, setOpenFig10] = React.useState(false);
    const [openFig11, setOpenFig11] = React.useState(false);
    const contentRef = React.useRef(null);

const markdown1 = `
## Introduction

When studying computer vision, you will sooner or later stumble across 
this algorithm famous for its robustness. Developed by Lowe and published 
in 2004, Scale-invariant Feature Transform (SIFT) is a well known algorithm 
for extracting invariant features from images to perform reliable matching. 
SIFT is invariant to image scaling, 
rotation and partially invariant to changes in illumination and 3D camera 
viewpoint. The major stages of computation include:

- **Scale-Space Extrema Detection:** Detect scale and orientation invariant 
local extrema over all scales and image locations efficiently by using a 
difference-of-Gaussian (DoG) function

- **Keypoint Localization:** Fit a detailed model at each candidate location 
to determine location and scale, and select keypoints based on measures of 
their stability

- **Orientation Assignment:** Assign one or more orientations to each keypoint 
location based on local image gradient directions

- **Keypoint Description:** Create a descriptor based on local image gradients 
measured at the selected scale in the region around each keypoint

SIFT is strongly heuristic, using a significant number of experimentally determined parameters. This article provides a gentle introduction to the algorithm, explaining both its underlying principles, the heuristics and the engineering tricks used for efficient implementation.

## Scale-Space Extrema Detection

The first stage of keypoint detection is to identify locations and scales that 
can be repeatably assigned under differing views of the same object. Stable 
features are identified across many scales using a multiscale image 
representation. For SIFT a Gaussian scale-space is used, which is basically 
a family of increasingly blurred images. The Gaussian filter acts as an approximation of the optical blur, and the Gaussian 
kernel approximates the camera’s point spread function. The blurring of the 
input image simulates the loss of detail produced when a scene is photographed 
from farther and farther. The scale-space, therefore, provides SIFT with scale 
invariance as it can be interpreted as the simulation of a set of snapshots 
of a given scene taken at different distances (different zoom factors/scales). There 
is a whole research field dedicated to explaining why simply downsampling the 
image is not as smart as using blurred images; check out “Scale-Space Theory 
in Computer Vision” from Tony Lindeberg.  

Mathematically, the scale-space of an image is defined as a function
$L(x,y,\\sigma)$ that is produced from the convolution of a variable-scale 
Gaussian $G(x,y,\\sigma)$ with an input grayscale image $I(x,y)$: 
$$
L(x,y,\\sigma)=G(x,y,\\sigma) \\ast I(x,y),
$$
where $\\ast$ is the convolution operation in $x$ and $y$, and
$$
G(x,y,\\sigma)=\\frac{1}{2\\pi \\sigma^2}e^{-({x^2+y^2})/2{\\sigma^2}}
$$

is the 2-D, isotropic (i.e. circularly symmetric) Gaussian with zero mean.
Note that this derivation is for a continuous image $I(x,y)$ with 
$(x,y)\\in\\mathbb{R}^2$. The unit of the scale $\\sigma$ is given in pixels. 
It essentially tells how many pixels wide the blur is: As $\\sigma$ becomes
larger the more values are considered for the smoothing. Since digital images 
are inherently discrete, the Gaussian smoothing is implemented as a discrete 
convolution using samples of a truncated Gaussian 
kernel.

Now let’s assume we have build the scale-space and we have a set of the same 
image with different blurs (scales). How do we chose the location and scale of 
the “best” keypoints based on those images? 

In SIFT the keypoint locations in scale-space are determined by the scale-space 
extrema of the difference-of-Gaussian (DoG) function convolved with the image denoted by
$D(x,y,\\sigma)$. This function provides an approximation of the of the 
scale-normalized Laplacian of Gaussians (NLoG) $\\sigma^2\\nabla^2G$. According 
to the original paper, the extrema of the NLoG are known to produce the most 
stable image features compared to a range of other possible image functions, 
such as the gradient, Hessian, or Harris corner function. Intuitively, the 
extrema of the NLoG detect blobs of different sizes. 
`;
const theLaplacianOperatorForImages1 = `
The Laplace operator or Laplacian, denoted by $\\nabla^2$ or 
$\\Delta$ is defined as the sum of pure second derivatives:
$$
\\nabla^2=\\Delta=\\frac{\\partial^2}{\\partial x^2}+\\frac{\\partial^2}{\\partial y^2}.
$$
The Laplacian of an image 
$$
\\nabla^2I=\\frac{\\partial^2I}{\\partial x^2}+\\frac{\\partial^2I}{\\partial y^2}
$$
highlights regions of rapid intensity change and can therefore be used for 
edge detection, i.e. by identifying the edges as the ‘zero-crossings’ of 
$\\nabla^2I$. The following Figure illustrates the idea for a 1-D blob $f(x)$:
`
const fig1Descp = `
Figure 1. First and second order derivatives $\\nabla f$ and $\\nabla^2f$ of 
a smooth box function $f(x)$. The edges of $f(x)$ can be identified as the 
extrema of $\\nabla f$ or the zero-crossings of $\\nabla^2f$.
`
const theLaplacianOperatorForImages2 = `
The Laplacian is highly sensitive to noise, because it approximates the 
second derivative measurement on the image. High-frequency, rapid intensity 
variations in the presence of noise cause large gradient changes, that make 
a confident identification of the zero-crossings impossible:
`
const fig2Descp = `
Figure 2. First and second order derivatives $\\nabla f$ and $\\nabla^2f$ of 
a noisy smooth box function $f(x)$. Noise makes reliable edge detection based 
on pure derivatives impossible.
`
const theLaplacianOperatorForImages3 = `
To counter this, the image is often smoothed first with a Gaussian before 
applying the Laplacian filter. This pre-processing step reduces the high 
frequency noise components prior to the differentiation step:
`
const fig3Descp = `
Figure 3. Smoothing the noisy box function $f(x)$ with a Gaussian $G$ removes
the high-frequency noise and allows to confidently detect the edges using the
first order derivative of the smoothed image $\\nabla (G \\ast f)$. 
`
const theLaplacianOperatorForImages4 = `
Since both the Gaussian filter and the Laplace operator are linear, we can 
save the extra step of smoothing the image by directly filtering the image 
with the Laplacian of Gaussian (LoG) $\\nabla^2G$:

$$
\\nabla^2(G\\ast I)=(\\nabla^2G)\\ast I
$$

The following Figure illustrates this idea for the 1-D Laplacian of Gaussian

$$
\\nabla^2G=\\frac{\\partial^2 G}{\\partial x^2}=\\frac{1}{\\sqrt{2\\pi 
\\sigma^2}}\\left(\\frac{x^2}{\\sigma^4}-\\frac{1}{\\sigma^2}\\right)e^{-\\frac{x^2}{2\\sigma^2}},
$$

which is simply the second derivative of the zero mean Gaussian:
`
const fig4Descp = `
Figure 4. Filtering the noisy box function $f(x)$ with the Laplacian of Gaussian 
$\\nabla^2 G$ allows reliable edge detection despite of noise. The zero-crossings
in the filtered output $(\\nabla^2 G)\\ast f$ corresponds to the edges of $f$.
The Laplacian of Gaussian is also known as Marr-Hildreth-Operator, Sombrero Filter
or Mexican Hat Filter. 
`
const theLaplacianOperatorForImages5 = `
We can extend this filter beyond edge detection to blob detection by using 
the scale-normalized Laplacian of Gaussians (NLoG)  $\\sigma^2 \\nabla^2G$. 
The normalization of LoG by $\\sigma^2$ is required for true scale invariance 
in scale-space as shwon by Lindberg [1994]. The intuition behind that is that 
the response of the $\\nabla^2G$ operator will depend on the scale $\\sigma$ 
that it used for the Gaussian $G$. As $\\sigma$ gets larger, its peak value 
begins to fall (the Gaussian gets “flatter”) and so does the response of the 
operator. Multiplying by $\\sigma^2$ counters this behaviour. The specific 
choice of the square of the scale parameter is not immediately obvious; a 
detailed justification can be found in “Scale-Space Theory for Computer Vision” 
(feel free to have a look and school me). 

Using the NLoG, we can detect blobs 
of different sizes by identifying its extrema in both scale $\\sigma$ and space 
$x$ and $y$. The following Figure illustrates how varying scales allow to 
detect blobs of different scales:
`
const fig5Descp = `
Figure 5. Detecting blobs of different scales with the normalized Laplacian of
Gaussian $\\sigma^2\\nabla^2 G$. Increasing the scale successively from 
$\\sigma_1$, $\\sigma_2$ to $\\sigma_3$ allows to detect blobs of increasing
scales as the extrema of the NLoG (marked with red points).
`
const markdown2 = `
The DoG $D(x,y,\\sigma)$ can be computed from the 
difference of two nearby scales $k\\sigma$ and $\\sigma$ separated by a 
constant multiplicative factor 
$k$:

$$
\\begin{aligned} 
D(x,y,\\sigma)
&=(D(x,y,k\\sigma)-D(x,y,\\sigma))\\ast I(x,y)\\\\
&=L(x,y,k\\sigma)-L(x,y,\\sigma).
\\end{aligned} 
$$

This is particularly efficient, as the smoothed images $L$ need to be 
computed in any case for scale-space feature description, and $D$ can therefore 
be computed by simple image subtraction.
`
const approximatingNLoGViaDoG = `
The relationship between $D$ and $\\sigma^2\\nabla^2G$ can be understood from 
the heat diffusion equation:
$$
\\frac{\\partial G(x,y,\\sigma)}{\\partial \\sigma}=\\sigma \\nabla^2 G(x,y,\\sigma).
$$
The key observation is that the family of Gaussians is the fundamental solution 
of the heat equation (Lindeberg [1994]). The left-hand side can be approximated 
by the finite difference approximation to $\\partial G/\\partial\\sigma$, using 
the difference of nearby scales at $k\\sigma$ and $\\sigma$:
$$
\\begin{aligned} 
\\sigma \\nabla^{2} G(x,y,\\sigma) &= \\frac{\\partial G(x,y,\\sigma)}{\\partial \\sigma} \\\\
&\\approx \\frac{G(x,y,k\\sigma) - G(x,y,\\sigma)}{k\\sigma - \\sigma}
\\end{aligned} 
$$
and therefore,
$$
G(x,y,k\\sigma) - G(x,y,\\sigma) \\approx (k - 1)\\sigma^{2} \\nabla^{2} G(x,y,\\sigma).
$$
When the DoG function is computed at scales that differ by a constant 
multiplicative factor $k$, it inherently incorporates the 
$\\sigma^2$ scale normalization required for the scale-invariant Laplacian. 
The factor $(k-1)$ in the equation is constant over all scales and therefore 
does not affect the extrema location.
`
const markdown3 = `
Now the question remains on how to efficiently construct the scale-space. 
What we need is a bunch of progressively blurred versions of the original 
image, which we can subtract from each other to detect the extrema at different 
scales and locations. The original work by Lowe proposes a constant increase 
of the scale by the factor $k$, so $\\sigma,k\\sigma, 2k\\sigma, 3k\\sigma$ etc.. 
To do this efficiently, each octave of the scale-space (i.e. doubling of 
$\\sigma$, so the interval between $\\sigma,2\\sigma, 4\\sigma, \\dots$) is divided 
into an integer number, $s$, of intervals. Then the scale sequence within an 
octave is:
$$
\\sigma,k\\sigma,k^2\\sigma,\\dots,k^s\\sigma.
$$
After $s$ steps we must reach the doubling:
$$
k^s\\sigma=2\\sigma.
$$
Solving for the constant factor we get $k=2^{1/s}$ as the distance between 
the scales. For an initial scale $\\sigma=1.6$ and $s=3$ integer number of 
intervals we have to compute the blurred images for the following values:
$$
1.60,2.02,2.54,3.20,4.03,\\dots.
$$
Note that the values for $\\sigma$ are rounded, e.g. $1.60 \ \\cdot \ 
2^{1/3}\\approx2.0159\\approx 2.02$. These values are chosen heuristically,
based on experimental observations in the original paper. 

A practical challenge of increasing scale is that it requires the use of 
progressively larger Gaussian kernels to filter the image. The kernel size 
is typically chosen to be the nearest odd integer to $6\\sigma+1$; consequently, 
increasing the scale leads to a linear increase in kernel size and, therefore, 
a quadratic increase in the number of kernel elements. Convolving a large 
image with a large kernel quickly becomes expensive. 

To address this issue, the scale can instead be held constant while the 
image is downsampled, which is equivalent to increasing $\\sigma$ while keeping 
the image size fixed. For this reason, once a complete octave has been processed, 
the Gaussian image whose scale has doubled relative to the initial $\\sigma$ is 
resampled by retaining every second pixel in both the row and column directions, 
after which the base scale is applied again. The sampling accuracy relative 
to $\\sigma$ is therefore no different from that at the start of the previous 
octave, while the computational cost is significantly reduced.

If all images were downsampled, however, computing differences between blurred 
images would no longer be straightforward, as adjacent scales would have 
different image sizes. Instead, the image is downsampled only after an entire 
octave has been processed, allowing adjacent image scales within each octave 
to be subtracted to form the DoG images. As a consequence, $s+3$ blurred images 
must be generated per octave to ensure that extrema detection spans a complete 
octave. The following Figure summarizes the scale-space creation:
`
const fig6Descp = `
Figure 1. Scale-space for the default configuration $\\sigma=1.6$ and  $s=3$, showing two octaves and the corresponding scales used 
for blurring the original image. Each octave contains $s + 3$ Gaussian-smoothed 
images $L$, of which two are auxiliary images required for extrema detection
(highlighted with a yellow border). After all blurred images for an octave are 
computed, the base image of the next octave is obtained by subsampling a 
previously computed blurred image (highlighted with a red border). The blurred 
images within each octave are generated successively using a constant 
incremental Gaussian smoothing $\\sigma_{\\text{inc}}$ across all scales. 
Subtracting adjacent blurred images produces a total of $s + 2$ DoG images $D$.
`
const markdown4 = `
Honestly, the algorithm becomes much clearer when examined at the implementation 
level. For clarity, a summary of the OpenCV implementation is provided below.
`
const openCVSIFT1 = `
In OpenCV, the function $\\color{#aa212e}{\\mathtt{SIFT\\_Impl\\!::\\!buildGaussianPyramid}}$ is responsible for 
generating the Gaussian-blurred images, which are later used to construct the 
DoG images and perform extrema detection. This function first precomputes the 
Gaussian scales using the following formula:
$$
\\sigma_{\\text{inc}}^2=\\sigma_{{i}}^2+\\sigma_{{i-1}}^2.
$$
This enables incremental Gaussian smoothing, since applying two successive 
digital Gaussian smoothings $G_{\\sigma_1}$ and $G_{\\sigma_2}$ approximately 
equals to applying one digital Gaussian smoothing with parameter 
$\\sigma_{\\text{inc}}=\\sqrt{\\sigma_1^2+\\sigma_2^2}$:
$$
G_{\\sigma_1}\\ast (G_{\\sigma_2}\\ast I)= G_{\\sigma_{\\text{inc}}}\\ast I.
$$
In the implementation, this is expressed as follows:
`
const openCVSIFTCode1 = `
~~~cpp
...
sig[0] = sigma;  // base sigma has defautl value of 1.6
double k = std::pow( 2., 1. / nOctaveLayers ); //nOctaveLayers is 3 by default
for( int i = 1; i < nOctaveLayers + 3; i++ )
{
    double sig_prev = std::pow(k, (double)(i-1))*sigma;
    double sig_total = sig_prev*k;
    sig[i] = std::sqrt(sig_total*sig_total - sig_prev*sig_prev);
}
...
~~~
`
const openCVSIFT2 = `
For the default values of $\\sigma_0=1.6$ and number of octave layers $s=3$,
the following $\\sigma_\\text{inc}$ are obtained:
`
const openCVSIFTCode2 = `
~~~cpp
k = 1.259921
Incremental sigmas (sig[i]):
sig[0] = 1.600000
sig[1] = 1.226273
sig[2] = 1.545008
sig[3] = 1.946588
sig[4] = 2.452547
sig[5] = 3.090016
~~~
`
const openCVSIFT3 = `
The Gaussian pyramid for each octave can now be constructed by incrementally 
blurring the image using the precomputed scales:
`
const openCVSIFTCode3 = `
~~~cpp
...
for( int o = 0; o < nOctaves; o++ )
{
    for( int i = 0; i < nOctaveLayers + 3; i++ )
    {
        // Pyramid is stored as one long vector
        Mat& dst = pyr[o*(nOctaveLayers + 3) + i];
        if( o == 0  &&  i == 0 )
            dst = base; 
        else if( i == 0 )
        {
            // Base of new octave dst is halved image from end of previous octave src
            const Mat& src = pyr[(o-1)*(nOctaveLayers + 3) + nOctaveLayers];
            resize(src, dst, Size(src.cols/2, src.rows/2),
                    0, 0, INTER_NEAREST);
        }
        else
        {
            const Mat& src = pyr[o*(nOctaveLayers + 3) + i-1];
            GaussianBlur(src, dst, Size(), sig[i], sig[i]);
        }
    }
}
...
~~~
`
const markdown5 = `
After constructing the scale-space, local maxima and minima of $D(x, y, \\sigma)$ 
are detected by comparing each sample point with its eight neighbors in the 
current image and the nine neighbors in each of the adjacent scales above and 
below. A point is selected only as extrema if it is greater than all of these 
neighbors or smaller than all of them:
`
const fig7Descp = `
Figure 2. Maxima and minima of the DoG images are detected by comparing a
pixel (in yellow) to its 26 neighbors in 3x3 regions at the current and 
adjacent scales (in gray). No extrema can be detected in the first
(last) scale of an octave because it has no lower (upper) scale image of the 
same size.
`
const markdown6 = `
The detected extrema of $D(x,y,\\sigma)$ in scale
and space are the output of this stage, which could be potential keypoints.
`
const markdown7 = `
## Keypoint Localization

After detecting the potential keypoint locations, they have to be refined and 
verified, whether they suffice as stable keypoints. Note that by comparing the 
pixel values from the neighbors on a specific scale as done in the previous 
stage, the extrema have discrete values. However, the ‘true’ extrema are rarely 
located exactly at integer grid locations and especially for high levels in 
the pyramid (where the image is already downsampled strongly), the sample 
spacing corresponds to large distances relative to the base image. Hence it 
is necessary to locate pixels to sub-pixel/sub-scale accuracy, and the original 
work references a prior paper stating that the refinement step has been shown 
to provide a substantial improvement to matching and stability. SIFT uses the 
quadratic Taylor expansion of the scale-space function $D(x,y,\\sigma)$ shifted 
so that the origin is located at the sample point being examined. In vector 
form, the expression is:
$$
\\begin{aligned} 
D(\\text{x})&=D+\\left(\\frac{\\partial D}{\\partial\\mathbf{x}}\\right)^\\top\\mathbf{x}\ 
+ \\frac{1}{2}\\mathbf{x}^\\top\\frac{\\partial D}{\\partial\\mathbf{x}}\\left(\\frac{\\partial D}{\\partial\\mathbf{x}}\\right)\\text{x}\\\\
&=D +(\\nabla D)^\\top\\mathbf{x} + \\frac{1}{2} \\mathbf{x}^\\top\\mathbf{{H}}\\mathbf{x}
\\end{aligned} 
$$
where $D(x=s,y=m,\\sigma=n)$ and its derivates are evaluated at the sample point 
$s,m,n$, $\\text{x}=(x,y,\\sigma)^\\top$ is the offset from that point, $\\nabla$ 
is the gradient operator,
$$
\\nabla D = \\frac{\\partial D}{\\partial \\mathbf{x}}= 
\\begin{bmatrix}
    \\frac{\\partial D}{\\partial x} \\\\[6pt]
    \\frac{\\partial D}{\\partial y} \\\\[6pt]
    \\frac{\\partial D}{\\partial \\sigma} \\\\
\\end{bmatrix}
$$
and $\\mathbf{H}$ is the Hessian matrix
$$
\\mathbf{H}= \\begin{bmatrix}
    \\frac{\\partial^2 D}{\\partial x ^2} &  \\frac{\\partial^2 D}{\\partial x \\partial y} &  \\frac{\\partial^2 D}{\\partial x \\partial \\sigma} \\\\[6pt]
    \\frac{\\partial^2 D}{\\partial y \\partial x} &  \\frac{\\partial^2 D}{\\partial y^2} &  \\frac{\\partial^2 D}{\\partial y \\partial \\sigma} \\\\[6pt]
    \\frac{\\partial^2 D}{\\partial \\sigma \\partial x} &  \\frac{\\partial^2 D}{\\partial \\sigma \\partial y} & \\frac{\\partial^2 D}{\\partial  \\sigma^2} \\\\
\\end{bmatrix}.
$$
The location $\\hat{\\text{x}}$ of the extremum is determined by taking the 
derivative of the Taylor expansion with respect to $\\text{x}$ and setting it 
to zero, which gives
$$
\\hat{\\text{x}}=-\\mathbf{H}^{-1} (\\nabla D).
$$
The Hessian and derivative of $D$ are approximated by using finite differences
of neighboring sample points. If the estimated offset $\\hat{\\text{x}}$ exceeds 
0.5 pixels in any direction, then it indicates, that the extremum lies closer 
to a different sample point. In such case, the sample point is updated and 
the interpolation is instead recomputed about the new location. 

The final offset $\\hat{\\text{x}}$ is then added to the coordinates of the 
selected sample point to obtain the interpolated estimate of the extremum 
location. For extrema detected in downsampled images, the resulting coordinates 
must be mapped back to the original image space by compensating for the 
octave-level downsampling.

In the verification stage, keypoints with low contrast or those that are 
poorly localized along edges are rejected. Low-contrast keypoints are particularly 
sensitive to noise: when the underlying image structure is weak, noise can 
become comparable to — or even dominate — the intensity variations caused by 
the feature itself. Moreover, image noise typically generates numerous spurious 
extrema in the DoG images. These extrema are unstable and are not associated 
with any meaningful image structure.

Such unstable extrema are discarded by thresholding their DoG response, ensuring 
that only keypoints with sufficient contrast and robustness are retained. 
Those unstable extrema are rejected when their DoG value
$$
D(\\hat{\\text{x}})=D+ \\frac{1}{2}(\\nabla D)^\\top \\hat{\\text{x}}
$$
is under a certain threshold, e.g. all extrema with a value of 
$|D(\\hat{\\text{x}})|$ less then $0.03$.

Candidate keypoints lying on edges are difficult to localize accurately and 
therefore unstable in the presence of even small amounts of noise. This is a 
direct consequence of the fact that an edge is invariant to translations along 
its principal axis. Such detections do not help define covariant keypoints — 
that is, keypoints whose detected location, scale, or orientation changes in a 
predictable (covariant) manner under geometric transformation of the image — 
and should be discarded. 

The two-dimensional Hessian of the DoG function provides a characterization 
of those undesirable keypoint candidates. A poorly defined peak in the DoG 
function will have a large principal curvature across the edge but a small 
one in the perpendicular direction. These principal curvatures are proportional 
to the eigenvalues of the Hessian matrix and measure the strength of curvature 
(second derivative) along the directions of maximum and minimum the curvature. 

Edges are characterized by a large principal curvature orthogonal to the 
edge and a small principal curvature along the edge. In terms of the 
Hessian eigenvalues, this corresponds to a large ration between the largest 
and the smallest eigenvalue. The principal curvature can be computed from 
a $2\\times2$ Hessian matrix, $\\mathbf{H}$, evaluated  at the location and scale of 
the keypoint:
$$
\\mathbf{H} = \\begin{bmatrix}
D_{xx} & D_{xy} \\\\
D_{xy} & D_{yy}
\\end{bmatrix}.
$$
The derivatives are estimated using finite differences of neighboring sample 
points. Since only the ratio of the eigenvalues is of interest, the explicit 
computation of the eigenvalues can be avoided. Let $\\alpha$ denote the eigenvalue 
with the largest magnitude, $\\beta$ the smaller one, and let $r$ be the ratio 
between the largest magnitude eigenvalue and the smaller one, such that $\\alpha=r\\beta$. 
Then:
$$
\\begin{aligned} 
\\text{Tr}(\\bm{H})&=D_{xx}+D_{yy}=\\alpha+\\beta
\\\\
\\text{Det}(\\bm{H})&=D_{xx}D_{yy}-(D_{xy})^2=\\alpha\\beta
\\\\
\\frac{\\text{Tr}(\\bm{H})^2}{\\text{Det}(\\bm{H})} &= \\frac{(\\alpha+\\beta)^2}{\\alpha\\beta}=\\frac{(r\\beta+\\beta)^2}{r\\beta^2}=\\frac{(r+1)^2}{r}.
\\end{aligned} 
$$
The quantity $(r + 1)^2 / r$ attains its minimum when the two eigenvalues are 
equal and increases monotonically with r. Therefore, to ensure that the ratio 
of the principal curvatures remains below a chosen threshold r, it suffices to 
check that
$$
\\frac{\\text{Tr}(\\bm{H})^2}{\\text{Det}(\\bm{H})} <\\frac{(r+1)^2}{r}
$$
is met.

This criterion is very efficient to evaluate, and the original work uses a value of r = 10.

As a result of these two stages, we obtain the keypoint location together with its corresponding scale:
`
const markdown8 = `
## Orientation Assignment

At this stage, the locations of the keypoints and their corresponding 
scales — that is, their characteristic sizes — have been determined. These 
keypoints are invariant with respect to scale, meaning that they can be 
reliably detected even when the image is zoomed in or out.

The next step is to introduce rotation invariance and to describe the 
local neighborhood around each keypoint. In the literature, rotation-invariant 
descriptors generally fall into two categories. On one hand, there are descriptors 
based on image properties that are inherently rotation invariant (for example, 
the structure tensor used in the Harris detector). On the other hand, there 
are descriptors that achieve rotation invariance through normalization with 
respect to a reference orientation.

The SIFT descriptor belongs to the latter category and relies on orientation 
normalization. The key idea is to assign a dominant orientation to each keypoint 
and to use this canonical orientation to normalize the descriptor. This allows 
to represent a keypoint relative
to its orientation and thus achieve invariance to image rotation.

To this end, the Gaussian-smoothed image $L(x,y,\\sigma)$ at the keypoint’s 
scale is considered. The gradient magnitude $m(x,y)$ and orientation $\\theta(x,y)$ 
are precomputed for each pixel in a neighborhood around the keypoint and 
accumulated into an orientation histogram.

The nieghborhood is selected as follows: The region is defined by a 
Gaussian-weighted circular window whose $\\sigma$ is 1.5 times the scale 
of the keypoint, that is, $\\sigma_w = \\lambda_{\\text{ori}}\\sigma=1.5\\sigma$. 
Note that the scale $\\sigma$ is measured in pixel units and effectively 
represents the amount of Gaussian blur applied. 

Since a Gaussian function has infinite support, it must be truncated in 
practice. Typically, values with a distance larger than $3\\sigma_w$ are 
neglected, so a Gaussian kernel of size $[6\\sigma_w][6\\sigma_w]$ is used. 
This is essentially the patch of samples contributing to the orientation 
computation. 

Within this patch, the gradient magnitude $m(x,y)$ and orientation $\\theta(x,y)$ 
are computed for each pixel using finite differences:

$$
\\begin{aligned}
m(x, y) &= \\sqrt{(L(x+1, y) - L(x-1, y))^{2} + (L(x, y+1) - L(x, y-1))^{2}}
\\\\
\\theta(x, y) &= \\tan^{-1}\\!\\left(\\frac{L(x, y+1) - L(x, y-1)}{L(x+1, y) - L(x-1, y)}\\right).
\\end{aligned}
$$

The computed orientation are accumulated into an orientation histogram with 
36 bins covering the full 360° range of orientation. Each sample contributes 
to the histogram with a weight proportional to its gradient magnitude $m(x,y)$ 
and the value of the Gaussian-weighted circular window with $\\sigma_w = 1.5\\sigma$. 
The contribution to bin $b$ of the orientation histogram $h$ is given by

$$
h(b)+= m(x,y)w(x,y),
$$

where the bin index $b$ is determined by the orientation $\\theta(x, y)$. 
The weights are computed as

$$
w(x,y)=\\exp\\left(\\frac{x^2+y^2}{2(\\lambda_{\\text{ori}}\\sigma)^2}\\right),
$$

so that pixels farther from the keypoint center contribute less to the 
orientation assignment. 

After the orientation histogram has been computed, it is smoothed to reduce 
sensitivity to quantization effects and noise. The peaks of the histogram 
are then identified, corresponding to the dominant directions of the local 
image gradients. The highest peak is selected first, and any other local 
peak whose magnitude is at least 80% of the maximum is also used to generate 
an additional keypoint with the corresponding orientation.

As a result, image locations exhibiting multiple strong gradient directions 
give rise to multiple keypoints that share the same location and scale but 
differ in orientation. Although only about 15% of keypoints are assigned 
multiple orientations, these cases contribute significantly to the robustness 
and stability of feature matching. Finally, to improve orientation accuracy, 
a parabola is fitted to the three histogram values nearest to each peak 
in order to interpolate the peak position.

The following Figure illustrates the output of this stage:
`
const markdown9 = `
## Keypoint Description

At this point, an image location, scale, and orientation have been computed 
for each keypoint. Together, these parameters define a repeatable local 
two-dimensional coordinate system in which the surrounding image region 
can be described, thereby providing invariance to translation, scale, and 
rotation. The next step is to compute a descriptor for this local image 
region that is highly distinctive while remaining as invariant as possible 
to other sources of variation, such as changes in illumination or 3D viewpoint.

The approach used by SIFT to construct descriptors is motivated by experimental 
evidence suggesting that local image gradients play a role similar to that 
of the human visual system in matching and recognizing 3D objects across 
different viewpoints.

First, a region of size $16 \\times 16$ pixels is centered at the keypoint. 
Within this region, the gradient magnitude and orientation are computed at 
each sample point, using the keypoint’s scale to select the appropriate level 
of Gaussian smoothing. The gradients are obtained using pixel differences. 
To achieve rotation invariance, both the descriptor coordinates and the 
gradient orientations are rotated relative to the keypoint’s assigned 
orientation.

A Gaussian weighting function with a standard deviation equal to half the 
size of the region is then applied to weight the contribution of each gradient 
magnitude. Although often illustrated as a circular window, this weighting 
falls off smoothly with distance from the center. Its purpose is to reduce 
sensitivity to small shifts in the window position and to downweight gradients 
far from the center of the descriptor, which are more susceptible to 
misregistration errors. Overall, this weighting emphasizes gradients near 
the center of the patch and produces a smoother, more stable descriptor.

Next, the $16 \\times 16$ region is divided into $4 \\times 4$ subregions, and 
the gradient orientations within each subregion are quantized into eight 
bins, corresponding to directions spaced 45° apart. This design allows 
for significant positional tolerance: a gradient sample may shift by up to 
four pixels and still contribute to the same histogram. Rather than assigning 
each sample entirely to a single orientation bin, SIFT uses trilinear 
interpolation to distribute the sample’s contribution across neighboring 
bins in space and orientation, in proportion to its distance from the bin 
centers. This interpolation avoids abrupt “boundary effects,” whereby small 
changes in orientation or position would otherwise cause large changes in 
the descriptor.

The eight orientation histogram values from each of the $4 \\times 4$ 
subregions are concatenated to form a 128-dimensional feature vector, which 
constitutes the SIFT descriptor. The following Figure illustrates the 
computations of this stage:
`
const fig8Descp = `
Figure 6. Computation of the 128-dimensional keypoint descriptor. The Gaussian weighting function
is indicated by a dotted circle; the keypoint is depicted in yellow.
`
const figSIFTStage2 = `
Figure 4. Left: Original image. Right: Detected keypoints and their corresponding scales overlaid on the grayscale image. 
The photograph depicts the Baroque sandstone sculpture of 
St. John of Nepomuk, dating from 1752, located on the Altmühl River in Middle 
Franconia. The statue stands on the bridge Altmühlbrücke, reflecting St. John of 
Nepomuk’s role as the patron saint of bridges and protector against drowning.
`
const figSIFTStage3 = `
Figure 5. Detected keypoints with their corresponding scales and orientations
depicted in yellow.
`
const markdown10 = `
Finally, to achieve rotation invariance, both the descriptor coordinates and the gradient orientations are rotated relative to the keypoint’s assigned orientation. To reduce the effects of illumination variations, the feature vector is normalized in two stages: first, it is normalized to unit length; then, the values in the normalized vector are thresholded so that no element exceeds 0.2, followed by a renormalization to unit length.

This procedure makes the resulting descriptor invariant to rotation, changes in image contrast and brightness, and certain nonlinear illumination effects. Although SIFT is not explicitly affine invariant, these properties provide robustness to small-to-moderate affine distortions in practice.
`
const descirptorQuantization1 = `
When examining OpenCV’s SIFT descriptors, you will notice that the values of 
the descriptors are not between 0 and 1 (as they should be after the last 
normalization step). The following Python code 
`
const descirptorQuantization2 = `
~~~python
...
gray = cv2.cvtColor(img,cv.COLOR_BGR2GRAY)

sift = cv2.SIFT_create(
    nfeatures=100,
    nOctaveLayers=3,
    contrastThreshold=0.1, 
    edgeThreshold=0.2,
    descriptorType=cv2.CV_32F,
    sigma=0.8)

kp, des = sift.detectAndCompute(gray,None)

print(des[0])
...    
~~~
outputs
`
const descirptorQuantization4 = `
~~~
[  5.  25.  24.  84.  55.   9.   2.   1.  27.  61.  12.  14.  18.  26.
  31.  15.  42.  39.   1.   5.  30.  24.  12.  21.   3.   5.   1.   2.
  69. 101.  41.   1.  20.  14.   7.  13. 137.  69.   4.   8. 137.  42.
   4.   4.  36.  44.  32. 122.  36.  25.   0.   3.  70. 132.  42.  29.
  15.  22.   4.  25.  21.  28.  22.   0.  30.  30.   9.  12. 137.  18.
   0.   5. 137.  52.  10.  15.  58.  17.   4.  37.  52.  33.  14.  98.
 137.  46.   4.   9.  12.  33.  10.  35.  49.   4.   1.   2.  29.  35.
   8.  22.  42.   5.   1.   7. 137.  29.   9.  18.  19.   7.   2.  36.
  15.  14.  10.  74.  71.  28.  18.   5.  15.   4.   1.   5.  16.  19.
  19.  22.]
~~~ 
`
const descirptorQuantization3 = `
This is because OpenCV and classical SIFT implementations quantize the 
normalized floating-point descriptor values into unsigned chars. This 
quantization reduces memory usage and significantly accelerates descriptor 
matching. Although, in theory, SIFT descriptors lie in the range [0, 1], 
in practice their effective dynamic range is much smaller: due to normalization 
and thresholding, most descriptor components lie within [0, 0.5].

Rather than encoding the full range [0, 1] $\\rightarrow$ [0, 255], the 
descriptor values are therefore mapped from [0, 0.5] $\\rightarrow$ [0, 255], 
where 255 is the maximum value representable by an 8-bit unsigned char. To 
achieve this mapping, a scaling factor of $255 / 0.5 \\approx 512$ is applied. 
This strategy effectively doubles the resolution in the range where descriptor
values actually occur, thereby minimizing quantization error while retaining 
the efficiency benefits of unsigned char storage.

In OpenCV's $\\color{#aa212e}{\\mathtt{calcSIFTDescirptor}}$ class the quantization is done as follows:
`
const openCVSIFTQuantization = `
~~~cpp
...
// factor used to convert floating-point descriptor to unsigned char
static const float SIFT_INT_DESCR_FCTR = 512.f;
...
float nrm2 = 0;
k = 0;
...
for( ; k < len_ddn; k++ )
    nrm2 += rawDst[k]*rawDst[k];    
const float thr = std::sqrt(nrm2)*SIFT_DESCR_MAG_THR;
i = 0, nrm2 = 0;
...
for( ; i < len_ddn; i++ )
{
    float val = std::min(rawDst[i], thr);
    rawDst[i] = val;
    nrm2 += val*val;
}
nrm2 = SIFT_INT_DESCR_FCTR/std::max(std::sqrt(nrm2), FLT_EPSILON);
...
for( ; k < len_ddn; k++ )
{
    dst[k] = saturate_cast<uchar>(rawDst[k]*nrm2);
}
...
~~~
`
const matching = `
## Matching

SIFT is commonly demonstrated through a keypoint matching task, where the 
objective is to establish correspondences between keypoints detected in 
two images. First, keypoints and their associated descriptors are extracted 
independently from both images. Matching is then performed by comparing each 
descriptor from the first image with all descriptors from the second image.

The similarity between SIFT descriptors is measured using the Euclidean 
distance. For each keypoint, the best candidate match is identified as its 
nearest neighbor, i.e., the descriptor with the minimum Euclidean distance.

To reduce the number of false matches, Lowe introduced an additional filtering 
step known as the ratio test. For each keypoint in the first image, the two 
closest matches are considered. The ratio test evaluates whether the distance 
to the nearest neighbor is sufficiently smaller than the distance to the second 
nearest neighbor. If this condition is not met, the keypoint is discarded and 
excluded from further processing.

This criterion is effective because correct matches typically exhibit a 
clear separation between the closest and the closest incorrect match, whereas 
ambiguous matches do not. Based on experimental evaluation, Lowe originally 
yproposed a ratio threshold of 0.8.

The following Python code demonstrates feature matching using SIFT.
`
const conclusion = `
## Final Remarks

As the preceding discussion shows, SIFT is a complex pipeline composed of 
many stages and empirically chosen constants. Owing to its robustness and 
reliability, SIFT remains widely used in numerous applications, including 
object recognition, visual SLAM, structure from motion, and panorama stitching. 
The algorithm was developed over a long period of empirical refinement, 
and although it was previously protected by patent, this patent expired in 2020.

More recent variants aim either to accelerate the algorithm, such as SURF, 
or to improve its robustness to more extreme geometric transformations, as 
in ASIFT.
`
const matchingCode = `
~~~python
...
sift = cv2.SIFT_create(
    nfeatures=500,
    nOctaveLayers=3,
    contrastThreshold=0.1, 
    edgeThreshold=0.2,
    descriptorType=cv2.CV_32F,
    sigma=1.6
)

gray1 = cv.cvtColor(img1, cv.COLOR_BGR2GRAY)
gray2 = cv.cvtColor(img2, cv.COLOR_BGR2GRAY)

kp1, des1 = sift.detectAndCompute(gray1, None)
kp2, des2 = sift.detectAndCompute(gray2, None)

# BFMatcher with default params
bf = cv.BFMatcher()
matches = bf.knnMatch(des1, des2, k=2)

# Apply ratio test
good = []
for m,n in matches:
    if m.distance < 0.8 * n.distance:
        good.append([m])
        
# Draw matches
img_matches = cv2.drawMatchesKnn(
	img1,
	kp1,
	img2,
	kp2,
	good,
	None,
	flags=cv2.DrawMatchesFlags_NOT_DRAW_SINGLE_POINTS
)
...
~~~
The matches are shown in the following Figure:
`
const matchesDescr = `
Figure 6. Brute-Force Matching with SIFT descriptors and ratio test.
`
const markdownLiterature = `
## References
`
const components = {
  greyblock: ({ children }) => (
    <div style={{ color: "#777" }}>{children}</div>
  ),
};

    useEffect(() => {
        if (!window.MathJax) {
            window.MathJax = {
            tex: {
    inlineMath: [["$", "$"]],
    displayMath: [["$$", "$$"]],
            },
            chtml: {
                linebreaks: {
                automatic: true,
                width: "container",
                },
            },
            };
        }

        window.MathJax?.typesetPromise?.();

        const onResize = () => {
            window.MathJax?.typesetClear?.();
            window.MathJax?.typesetPromise?.();
        };

        window.addEventListener("resize", onResize);

        return () => window.removeEventListener("resize", onResize);
    }, []);

    return (    
    <div className='article-row'>
        <div className="article-card">
            <h1 className="article-title">Demystifying Scale-Invariant Feature Transform</h1>
            <hr className="article-hr"/>
            <div className="article-post-layout">
                <aside className="article-toc">
                    <TableOfContent 
                        contentRef={contentRef} 
                        selector="h2, h3" 
                    />
                </aside>
                <div className="article-post-content">
                    <div className="article-text">
                        <div className="md" ref={contentRef}>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown1}
                    </Markdown>
                    <details>
                        <summary className="gray-summary">
                            The Laplace Operator for Images
                        </summary>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {theLaplacianOperatorForImages1}
                        </Markdown>
                        <div style={{ margin: "20px 0", textAlign: "center" }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/derivatives.png"
                                alt="SIFT scale-space illustration"
                                style={{
                                    width: "100%",
                                    maxWidth: "400px",
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig1(true)}
                            />
                            <figcaption
                                style={{
                                marginTop: "1px",
                                fontSize: "0.9em",
                                color: "#555",
                                maxWidth: "400px",
                                marginLeft: "auto",
                                marginRight: "auto",
                                textAlign: "left"
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {fig1Descp}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig1}
                                close={() => setOpenFig1(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/derivatives.png"
                            />
                        </div>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {theLaplacianOperatorForImages2}
                        </Markdown>
                        <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/noisy_derivatives.png"
                                alt="SIFT scale-space illustration"
                                style={{
                                    width: "100%",
                                    maxWidth: "400px",
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig2(true)}
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '400px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {fig2Descp}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig2}
                                close={() => setOpenFig2(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/noisy_derivatives.png"
                            />
                        </div>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {theLaplacianOperatorForImages3}
                        </Markdown>
                        <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/noisy_smoothed_derivatives.png"
                                alt="SIFT scale-space illustration"
                                style={{ 
                                    width: '95%', 
                                    maxWidth: '433px', 
                                    minWidth: '200px', 
                                    marginLeft: '-33px',
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig3(true)}
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '400px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {fig3Descp}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig3}
                                close={() => setOpenFig3(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/noisy_smoothed_derivatives.png"
                            />
                        </div>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {theLaplacianOperatorForImages4}
                        </Markdown>
                        <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/noisy_laplacian_smoothed.png"
                                alt="SIFT scale-space illustration"
                                style={{ 
                                    width: '95%', 
                                    maxWidth: '440px', 
                                    minWidth: '200px', 
                                    marginLeft: '-40px',
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig4(true)}
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '400px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {fig4Descp}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig4}
                                close={() => setOpenFig4(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/noisy_laplacian_smoothed.png"
                            />
                        </div>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {theLaplacianOperatorForImages5}
                        </Markdown>
                        <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/multiscale_blob.png"
                                alt="SIFT scale-space illustration"
                                style={{ 
                                    width: '95%', 
                                    maxWidth: '100%', 
                                    minWidth: '200px', 
                                    marginLeft: '-40px',
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig5(true)} 
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '400px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {fig5Descp}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig5}
                                close={() => setOpenFig5(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/multiscale_blob.png"
                            />
                        </div>
                    </details>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown2}
                    </Markdown>
                    <details>
                        <summary className="gray-summary">
                            Approximating NLoG via DoG
                        </summary>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {approximatingNLoGViaDoG}
                        </Markdown>
                    </details>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown3}
                    </Markdown>
                        <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT1.png"
                                alt="SIFT scale-space illustration"
                                style={{ 
                                    width: '100%', 
                                    maxWidth: '1000px',
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig6(true)}
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '1000px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {fig6Descp}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig6}
                                close={() => setOpenFig6(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT1.png"
                            />
                        </div>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown4}
                    </Markdown>
                    <details>
                        <summary className="gray-summary">
                            OpenCV's Scale-Space Creation for SIFT
                        </summary>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {openCVSIFT1}
                        </Markdown>
                        <Markdown
                            components={{
                                code({ children, className, ...rest }) {
                                const match = /language-(\w+)/.exec(className || '')
                                return match ? (
                                    <SyntaxHighlighter
                                    {...rest}
                                    PreTag="div"
                                    language={match[1]}   // "cpp"
                                    style={vscDarkPlus}
                                    showLineNumbers
                                    >
                                    {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                ) : (
                                    <code {...rest}>{children}</code>
                                )
                                }
                            }}
                        >
                            {openCVSIFTCode1}
                        </Markdown>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {openCVSIFT2}
                        </Markdown>
                        <Markdown
                            components={{
                                code({ children, className, ...rest }) {
                                const match = /language-(\w+)/.exec(className || '')
                                return match ? (
                                    <SyntaxHighlighter
                                    {...rest}
                                    PreTag="div"
                                    style={vscDarkPlus}
                                    >
                                    {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                ) : (
                                    <code {...rest}>{children}</code>
                                )
                                }
                            }}
                        >
                            {openCVSIFTCode2}
                        </Markdown>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {openCVSIFT3}
                        </Markdown>
                        <Markdown
                            components={{
                                code({ children, className, ...rest }) {
                                const match = /language-(\w+)/.exec(className || '')
                                return match ? (
                                    <SyntaxHighlighter
                                    {...rest}
                                    PreTag="div"
                                    language={match[1]}   // "cpp"
                                    style={vscDarkPlus}
                                    showLineNumbers
                                    >
                                    {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                ) : (
                                    <code {...rest}>{children}</code>
                                )
                                }
                            }}
                        >
                            {openCVSIFTCode3}
                        </Markdown>
                    </details>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown5}
                    </Markdown>
                    <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT2.png"
                                alt="SIFT scale-space illustration"
                                style={{ 
                                    width: '100%', 
                                    maxWidth: '500px', 
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig7(true)}
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '500px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {fig7Descp}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig7}
                                close={() => setOpenFig7(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT2.png"
                            />
                        </div>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown6}
                    </Markdown>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown7}
                    </Markdown>
                        <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT_stage2.png"
                                alt="SIFT scale-space illustration"
                                style={{ 
                                    width: '100%', 
                                    maxWidth: '1000px', 
                                    cursor: "zoom-in"
                                }}
                                onClick={() => setOpenFig8(true)}
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '1000px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {figSIFTStage2}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig8}
                                close={() => setOpenFig8(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT_stage2.png"
                            />
                        </div>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown8}
                    </Markdown>
                    <div style={{ margin: '20px 0', textAlign: 'center' }}>
                            <img
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/keypoints_scale_location_orientation.jpg"
                                alt="SIFT scale-space illustration"
                                style={{ 
                                    width: '100%', 
                                    maxWidth: '500px',
                                    cursor: "zoom-in" 
                                }}
                                onClick={() => setOpenFig9(true)}
                            />
                            <figcaption 
                                style={{
                                    marginTop: '1px',
                                    fontSize: '0.9em',
                                    color: '#555',
                                    maxWidth: '500px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    textAlign: 'left'
                                }}
                            >
                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                    {figSIFTStage3}
                                </Markdown>
                            </figcaption>
                            <BlogLightbox
                                open={openFig9}
                                close={() => setOpenFig9(false)}
                                src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/keypoints_scale_location_orientation.jpg"
                            />
                        </div>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown9}
                    </Markdown>
                    <div style={{ margin: '20px 0', textAlign: 'center' }}>
                        <img
                            src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT3.png"
                            alt="SIFT scale-space illustration"
                            style={{ 
                                width: '100%', 
                                maxWidth: '600px',
                                cursor: "zoom-in" 
                            }}
                            onClick={() => setOpenFig10(true)}
                        />
                        <figcaption 
                            style={{
                                marginTop: '1px',
                                fontSize: '0.9em',
                                color: '#555',
                                maxWidth: '600px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                textAlign: 'left'
                            }}
                        >
                            <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                {fig8Descp}
                            </Markdown>
                        </figcaption>
                        <BlogLightbox
                            open={openFig10}
                            close={() => setOpenFig10(false)}
                            src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT3.png"
                        />
                    </div>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdown10}
                    </Markdown>
                    <details>
                        <summary className="gray-summary">
                            Descriptor Quantization in OpenCV 
                        </summary>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {descirptorQuantization1}
                        </Markdown>
                        <Markdown
                            components={{
                                code({ children, className, ...rest }) {
                                return (
                                    <SyntaxHighlighter
                                    {...rest}
                                    PreTag="div"
                                    language={'python'}  
                                    style={vscDarkPlus}
                                    showLineNumbers
                                    >
                                    {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                )
                                }
                            }}
                        >
                            {descirptorQuantization2}
                        </Markdown>
                        <Markdown
                            components={{
                                code({ children, className, ...rest }) {
                                return (
                                    <SyntaxHighlighter
                                    {...rest}
                                    PreTag="div"
                                    style={vscDarkPlus}
                                    >
                                    {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                )
                                }
                            }}
                        >
                            {descirptorQuantization4}
                        </Markdown>
                        <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {descirptorQuantization3}
                        </Markdown>
                        <Markdown
                            components={{
                                code({ children, className, ...rest }) {
                                const match = /language-(\w+)/.exec(className || '')
                                return match ? (
                                    <SyntaxHighlighter
                                    {...rest}
                                    PreTag="div"
                                    language={match[1]}   // "cpp"
                                    style={vscDarkPlus}
                                    showLineNumbers
                                    >
                                    {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                ) : (
                                    <code {...rest}>{children}</code>
                                )
                                }
                            }}
                        >
                            {openCVSIFTQuantization}
                        </Markdown>
                    </details>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {matching}
                    </Markdown>
                    <Markdown
                        components={{
                            code({ children, className, ...rest }) {
                            return (
                                <SyntaxHighlighter
                                {...rest}
                                PreTag="div"
                                language={'python'}  
                                style={vscDarkPlus}
                                showLineNumbers
                                >
                                {String(children).replace(/\n$/, '')}
                                </SyntaxHighlighter>
                            )
                            }
                        }}
                    >
                        {matchingCode}
                    </Markdown>
                    <div style={{ margin: '20px 0', textAlign: 'center' }}>
                        <img
                            src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT_matches.png"
                            alt="SIFT scale-space illustration"
                            style={{ 
                                width: '100%', 
                                maxWidth: '1000px',
                                cursor: "zoom-in" 
                            }}
                            onClick={() => setOpenFig11(true)}
                        />
                        <figcaption 
                            style={{
                                marginTop: '1px',
                                fontSize: '0.9em',
                                color: '#555',
                                maxWidth: '1000px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                textAlign: 'left'
                            }}
                        >
                            <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                {matchesDescr}
                            </Markdown>
                        </figcaption>
                        <BlogLightbox
                            open={openFig11}
                            close={() => setOpenFig11(false)}
                            src="https://d3rnpbgh9zefuh.cloudfront.net/SIFT/SIFT_matches.png"
                        />
                    </div>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {conclusion}
                    </Markdown>
                    <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {markdownLiterature}
                    </Markdown>
                    <div className="gray-summary">
                        <p>
                            D. G. Lowe; Distinctive Image Features From Scale-Invariant Keypoints;
                            International Journal of Computer Vision; vol. 60; no. 2;
                            pp. 91–110, 2004.
                        </p>

                        <p>
                            I. Rey-Otero, M. Delbracio; Anatomy of the SIFT Method;
                            Image Processing On Line; vol. 4; pp. 370–396; 2014.
                        </p>

                        <p>
                            R. C. Gonzalez, R. E. Woods; Digital Image Processing; 4th ed.
                            Pearson, 2018; Chapter 11; Scale-Invariant Feature Transform (SIFT).
                        </p>

                        <p>
                            OpenCV Documentation; Introduction to SIFT (Scale-Invariant Feature Transform); {" "}
                            <a
                            href="https://docs.opencv.org/4.x/da/df5/tutorial_py_sift_intro.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            >
                             https://docs.opencv.org/4.x/da/df5/tutorial_py_sift_intro.html
                            </a>
                            ; accessed Jan. 2026.
                        </p>

                        <p>
                            S. K. Nayar; SIFT Algorithm; First Principles of Computer Vision; {" "}
                            lecture series; YouTube; {" "}
                            <a
                            href="https://www.youtube.com/watch?v=KgsHoJYJ4S8"
                            target="_blank"
                            rel="noopener noreferrer"
                            >
                            https://www.youtube.com/watch?v=KgsHoJYJ4S8
                            </a>
                            ; accessed Jan. 2026.
                        </p>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
}
